// src/components/ListaServicios.tsx
//
// Servicios de la landing del profesional: tarjetas planas, cada una con su
// tono de color.
//
// HISTORIA (oct-2026, pedidos de Esteban):
//   - Las tarjetas originales tenían cuatro acentos compitiendo —precio en
//     pastilla lila, seña en naranja y mayúsculas, duración en chip gris y botón
//     en degradé— y todo centrado por el estilo global de la app. Se leía
//     desordenado y no mandaba nada.
//   - Primero se pasó a una lista estilo iOS. La jerarquía quedó bien, pero él
//     prefirió volver a tarjetas "con un poco de colores".
//   - Después probamos un tono distinto por tarjeta; eligió que fueran todas
//     del mismo color, el celeste de la primera.
// Esta versión se queda con la jerarquía de la lista y la pone en tarjetas:
//   1. el nombre del servicio, y a la derecha el precio;
//   2. una sola línea: duración · cómo se paga;
//   3. la descripción, con "leer más";
//   4. el botón abajo, a todo el ancho, a la misma altura en toda la fila.
// El color está en la superficie (celeste pálido) y en el botón y el ícono
// (azul fuerte), nunca en el texto: suma vida sin volver a competir con lo que
// hay que leer.
//
// Las tarjetas entran escalonadas cuando aparecen en pantalla, no al cargar:
// en el celular la sección está más abajo, y una animación al cargar pasaría
// sin que nadie la vea.

import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Clock } from 'lucide-react';
import { PriceMode } from '../services/api';
import { displayPrice, formatARS, isChargeable } from '../utils/priceDisplay';
import TextoExpandible from './TextoExpandible';
import './ListaServicios.css';

export interface ServicioListable {
  id: number;
  name: string;
  description?: string;
  duration: number | string;
  price: number | string;
  price_mode?: PriceMode;
}

interface PoliticaDePago {
  tipo_cobro: 'solo_sena' | 'consulta_completa' | 'efectivo';
  porcentaje_sena: number;
}

interface Props<T extends ServicioListable> {
  servicios: T[];
  politicaDePago: PoliticaDePago | null;
  disponible: boolean;
  onAgendar: (servicio: T) => void;
}

// Cómo se paga, en pocas palabras. Solo para los servicios que se cobran por
// la app: los de obra social o "a consultar" no llevan seña.
const comoSePaga = (s: ServicioListable, politica: PoliticaDePago | null): string | null => {
  if (!politica || !isChargeable(s)) return null;
  switch (politica.tipo_cobro) {
    case 'solo_sena': {
      const precio = typeof s.price === 'string' ? parseFloat(s.price) || 0 : s.price;
      return `Seña ${formatARS(Math.round(precio * (politica.porcentaje_sena / 100)))}`;
    }
    case 'consulta_completa':
      return 'Se paga al reservar';
    case 'efectivo':
      return 'Pagás en el consultorio';
    default:
      return null;
  }
};

function ListaServicios<T extends ServicioListable>({ servicios, politicaDePago, disponible, onAgendar }: Props<T>) {
  const listaRef = useRef<HTMLUListElement>(null);
  const [aLaVista, setALaVista] = useState(false);

  useEffect(() => {
    const lista = listaRef.current;
    if (!lista || !('IntersectionObserver' in window)) {
      setALaVista(true);
      return;
    }
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas[0]?.isIntersecting) {
          setALaVista(true);
          observador.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    observador.observe(lista);
    return () => observador.disconnect();
  }, []);

  return (
    <ul ref={listaRef} className={`servicios-grilla ${aLaVista ? 'is-visible' : 'is-oculta'}`}>
      {servicios.map((s, i) => {
        const pago = comoSePaga(s, politicaDePago);
        return (
          <li key={s.id} className="servicio-tarjeta" style={{ '--i': i } as React.CSSProperties}>
            <div className="servicio-cabecera">
              <h3 className="servicio-nombre">{s.name}</h3>
              <span className={isChargeable(s) ? 'servicio-precio' : 'servicio-precio servicio-precio--suave'}>
                {displayPrice(s)}
              </span>
            </div>
            <p className="servicio-meta">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
              <span>{s.duration} min</span>
              {pago && (
                <>
                  <span className="servicio-punto" aria-hidden="true">·</span>
                  <span>{pago}</span>
                </>
              )}
            </p>
            {s.description && (
              <TextoExpandible text={s.description} limit={110} className="servicio-descripcion" />
            )}
            <div className="servicio-pie">
              {disponible ? (
                <button type="button" className="servicio-agendar" onClick={() => onAgendar(s)}>
                  Agendar
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              ) : (
                <span className="servicio-no-disponible">No disponible por ahora</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default ListaServicios;
